import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo } from '../data/mock';
import { Instagram, Facebook, MapPin, Phone, Mail, ArrowUp, BookOpen, Clock } from 'lucide-react';
import { trackCallClick, trackEmailClick } from '../lib/trackCall';

const Footer = () => {
  const { language, toggleLanguage, t } = useLanguage();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const quickLinks = [
    { key: 'home', id: 'home' },
    { key: 'about', id: 'about' },
    { key: 'products', id: 'products' },
    { key: 'customMix', id: 'custom-mix' },
    { key: 'portfolio', id: 'portfolio' },
    { key: 'contact', id: 'contact' }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <img
              src={companyInfo.logo}
              alt="Pigmentos"
              className="h-16 w-auto mb-6"
            />
            <p className="text-gray-400 leading-relaxed mb-6">
              {t('footer.description')}
            </p>
            {/* As redes NÃO ficam aqui: vivem na coluna da direita, sob "Siga-nos". Estavam nos
                dois sítios ao mesmo tempo. */}
          </div>

          {/* Quick Links Column */}
          <div>
            <h4 className="text-lg font-bold mb-6">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.key}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-gray-400 hover:text-orange-500 transition-colors duration-200"
                  >
                    {t(`nav.${link.key}`)}
                  </button>
                </li>
              ))}
            </ul>

            {/* Páginas de tema. Existem para responder a perguntas concretas que as pessoas
                escrevem no Google ("tinta para telhados") e a que a página única do site não
                conseguia responder. Ligar daqui não é decoração: uma página a que ninguém liga
                é uma página órfã, e o Google trata-a como tal. */}
            <h4 className="text-lg font-bold mt-8 mb-4">
              {language === 'en' ? 'Guides' : 'Guias'}
            </h4>
            {/* A lista vive aqui e em _scripts/gerar_pagina_guias.py (páginas estáticas).
                Guia nova entra nos dois sítios. */}
            <ul className="space-y-2">
              <li>
                <a
                  href="/tintas-cin/"
                  className="text-gray-400 text-sm hover:text-orange-500 transition-colors"
                >
                  {language === 'en' ? 'CIN paints in Oeiras' : 'Tintas CIN em Oeiras'}
                </a>
              </li>
              <li>
                <a
                  href="/tintas-robbialac/"
                  className="text-gray-400 text-sm hover:text-orange-500 transition-colors"
                >
                  {language === 'en' ? 'Robbialac paints in Oeiras' : 'Tintas Robbialac em Oeiras'}
                </a>
              </li>
              <li>
                <a
                  href="/tintas-matesica/"
                  className="text-gray-400 text-sm hover:text-orange-500 transition-colors"
                >
                  {language === 'en' ? 'Matesica paints in Oeiras' : 'Tintas Matesica em Oeiras'}
                </a>
              </li>
              <li>
                <a
                  href="/microcimento/"
                  className="text-gray-400 text-sm hover:text-orange-500 transition-colors"
                >
                  {language === 'en' ? 'Microcement in Oeiras' : 'Microcimento em Oeiras'}
                </a>
              </li>
              <li>
                <a
                  href="/tinta-para-madeira-e-ferro/"
                  className="text-gray-400 text-sm hover:text-orange-500 transition-colors"
                >
                  {language === 'en' ? 'Wood and iron paint in Oeiras' : 'Tinta para madeira e ferro em Oeiras'}
                </a>
              </li>
              <li>
                <a
                  href="/tinta-para-telhados/"
                  className="text-gray-400 text-sm hover:text-orange-500 transition-colors"
                >
                  {language === 'en' ? 'Roof paint in Oeiras' : 'Tinta para telhados em Oeiras'}
                </a>
              </li>
              <li>
                <a
                  href="/guias/"
                  className="text-gray-400 text-sm hover:text-orange-500 transition-colors font-medium"
                >
                  {language === 'en' ? 'All guides' : 'Ver todos os guias'}
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="text-lg font-bold mb-6">{t('footer.contact')}</h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                <span className="text-gray-400">{companyInfo.address}</span>
              </li>
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                <div className="space-y-1">
                  <a
                    href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                    onClick={() => trackCallClick('rodapé', companyInfo.phone)}
                    className="block text-gray-400 hover:text-orange-500 transition-colors"
                  >
                    {companyInfo.phone}
                  </a>
                  <a
                    href={`tel:${companyInfo.phone2.replace(/\s/g, '')}`}
                    onClick={() => trackCallClick('rodapé', companyInfo.phone2)}
                    className="block text-gray-400 hover:text-orange-500 transition-colors"
                  >
                    {companyInfo.phone2}
                  </a>
                  <a
                    href={`tel:${companyInfo.landline.replace(/\s/g, '')}`}
                    onClick={() => trackCallClick('rodapé', companyInfo.landline)}
                    className="block text-gray-400 hover:text-orange-500 transition-colors"
                  >
                    {companyInfo.landline}
                  </a>
                </div>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-orange-500 flex-shrink-0" />
                <a
                  href={`mailto:${companyInfo.email}`}
                  onClick={() => trackEmailClick('rodapé')}
                  className="text-gray-400 hover:text-orange-500 transition-colors"
                >
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Clock className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                <div className="text-gray-400 text-sm">
                  {/* O "não fecha ao almoço" é uma característica dos DIAS ÚTEIS e estava colado
                      ao domingo, onde se lia como se a loja não fechasse ao almoço no dia em que
                      está encerrada. Passou para debaixo da linha a que pertence. */}
                  {language === 'en' ? (
                    <>
                      <p>Mon to Fri: 7:30 AM to 6:30 PM</p>
                      <p className="text-gray-500">Open through lunchtime</p>
                      <p>Saturday: 8:00 AM to 1:00 PM</p>
                      <p className="text-gray-500">Sunday: closed</p>
                    </>
                  ) : (
                    <>
                      <p>Segunda a sexta: 7h30 às 18h30</p>
                      <p className="text-gray-500">Não fecha ao almoço</p>
                      <p>Sábado: 8h às 13h</p>
                      <p className="text-gray-500">Domingo: encerrado</p>
                    </>
                  )}
                </div>
              </li>
            </ul>

          </div>

          {/* Coluna do idioma.
              Era a coluna "Siga-nos" e estava trocada: o título dizia "Siga-nos" e a primeira
              coisa lá dentro era o seletor de idioma. Pior, as redes apareciam DUAS vezes no
              mesmo rodapé — os ícones na coluna da marca e outra vez aqui, em texto.
              Ficou uma coisa só por sítio: as redes vivem na coluna da marca (com o @ ao lado),
              e esta coluna é o que sempre foi na prática, o idioma. */}
          <div>
            <h4 className="text-lg font-bold mb-6">Idioma / Language</h4>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => toggleLanguage('pt')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  language === 'pt'
                    ? 'bg-orange-600 text-white'
                    : 'bg-white/10 text-gray-400 hover:bg-white/20'
                }`}
              >
                PT 🇵🇹
              </button>
              <button
                onClick={() => toggleLanguage('en')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  language === 'en'
                    ? 'bg-orange-600 text-white'
                    : 'bg-white/10 text-gray-400 hover:bg-white/20'
                }`}
              >
                EN 🇬🇧
              </button>
            </div>

            {/* Siga-nos — por baixo do idioma, e é o ÚNICO sítio do rodapé onde as redes aparecem. */}
            <h4 className="text-lg font-bold mt-8 mb-4">{t('footer.followUs')}</h4>
            <p className="text-sm text-gray-500 mb-3">@pigmentosoeiras</p>
            <div className="flex items-center space-x-3">
              <a
                href={companyInfo.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-orange-600 transition-colors duration-300"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href={companyInfo.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-orange-600 transition-colors duration-300"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} {companyInfo.name}. {t('footer.rights')}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-gray-400">
              <a href="/privacidade" className="hover:text-orange-500 transition-colors">Política de Privacidade</a>
              <a href="/privacidade#cookies" className="hover:text-orange-500 transition-colors">Política de Cookies</a>
              <a href="/termos" className="hover:text-orange-500 transition-colors">Termos e Condições</a>
              <a
                href="https://www.livroreclamacoes.pt/inicio/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 hover:text-orange-500 transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                Livro de Reclamações
              </a>
            </div>
            <button
              onClick={scrollToTop}
              className="w-10 h-10 bg-orange-600 rounded-lg flex items-center justify-center hover:bg-orange-700 transition-colors duration-300 flex-shrink-0"
            >
              <ArrowUp className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
